import { CardHeaderProps } from '@/components/card';
import { PlanFunds } from '@/services/Investment.service';
import { PostProgramChangeReenrollmentBody } from '@/services/ops/investments/Program.service';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';

import { useFormik } from 'formik';
import { sum } from 'lodash';
import { FC, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBeforeUnload, useToggle } from 'react-use';

import { ProgramChangeReview } from './ProgramChanges';
import { ChangeMapping } from './re-enrollment/InvestmentAutocomplete.component';

type ProgramReenrollPageProps = PlanFunds & {
  onSubmit: ({
    jiraTicket,
    effectiveDate,
    mappings
  }: Omit<
    PostProgramChangeReenrollmentBody,
    'reenrollmentType'
  >) => Promise<void>;
  planId: number;
};

export const ProgramReenrollPage: FC<ProgramReenrollPageProps> = props => {
  const [isOpen, toggleOpen] = useToggle(false);
  const [isConfirming, toggleConfirming] = useToggle(false);
  const [searchParams] = useSearchParams();

  const formik = useFormik({
    initialValues: {
      changes: []
    },
    onSubmit: async () => {
      await props.onSubmit({
        effectiveDate: searchParams.get('effectiveDate'),
        jiraTicket: searchParams.get('effectiveDateNote'),
        mappings: formik.values.changes
      });
    }
  });

  const dirtyFn = useCallback(() => formik.dirty, [formik.dirty]);

  useBeforeUnload(
    dirtyFn,
    'Are you sure you want to leave and remove all changes?'
  );

  const currentProgramMappingsCount = useMemo(() => {
    const targetMappingsCount = props.currentProgram.targetModels?.length || 0;
    const riskMappingsCount = props.currentProgram.riskModels?.length || 0;
    const fundLineupMappingsCount =
      props.currentProgram.fundLineup?.fundLineupFunds?.length || 0;
    const goalMappingCount =
      props.currentProgram.goalSeries?.goePortfolioIds?.length || 0;
    const nliMappingCount = props.currentProgram.nliModels?.length || 0;
    const isMSMAMapped = props.currentProgram.externalProviders.some(
      i => 'ms_ma_menu' === i.investmentType
    )
      ? 1
      : 0;
    return sum([
      targetMappingsCount,
      riskMappingsCount,
      fundLineupMappingsCount,
      goalMappingCount,
      nliMappingCount,
      isMSMAMapped
    ]);
  }, [props.currentProgram]);

  const isAllMappingsDone = useMemo(() => {
    const fundLineupMappingsCount =
      formik.values.changes.find(change => change.oldType === 'pmenu')?.mappings
        ?.length || 0;
    return (
      currentProgramMappingsCount ===
      sum([
        formik.values.changes.filter(change => change.oldType !== 'pmenu')
          .length,
        fundLineupMappingsCount
      ])
    );
  }, [currentProgramMappingsCount, formik.values.changes]);

  const actionButtons = useMemo(() => {
    return [
      formik.dirty && {
        label: 'Reset',
        onClick: () => {
          toggleOpen(true);
        },
        variant: 'text'
      },
      {
        disabled: !formik.dirty || !isAllMappingsDone,
        label: 'Review',
        onClick: () => {
          toggleConfirming();
        },
        variant: 'contained'
      }
    ].filter(Boolean) as CardHeaderProps['actionButtonsProps'];
  }, [formik.dirty, isAllMappingsDone]);

  const onKeepEditing = useCallback(() => {
    toggleOpen(false);
  }, []);

  const onReset = useCallback(() => {
    formik.resetForm();
    toggleOpen(false);
  }, []);

  return (
    <>
      <Dialog open={isOpen}>
        <DialogContent>
          <DialogTitle>Reset and remove all changes?</DialogTitle>
          <DialogActions>
            <Button onClick={onKeepEditing}>Keep Editing</Button>
            <Button color='error' onClick={onReset} variant='contained'>
              Reset
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <ProgramChangeReview
        confirmButtonProps={[
          {
            label: 'Back To Edit',
            onClick: () => {
              toggleConfirming();
            },
            variant: 'text'
          },
          {
            label: 'Submit for Approval',
            onClick: () => {
              formik.submitForm();
            },
            variant: 'contained'
          }
        ]}
        currentProgram={props.currentProgram}
        effectiveDate={searchParams.get('effectiveDate')}
        effectiveDateNote={searchParams.get('effectiveDateNote')}
        isConfirming={isConfirming}
        mappingButtonProps={actionButtons}
        mappingTableContext={{
          changes: formik.values.changes,
          setChanges: (changes: ChangeMapping[]) =>
            formik.setFieldValue('changes', changes)
        }}
        name={props.name}
        newProgram={props.newProgram}
        planId={props.planId}
      />
    </>
  );
};

ProgramReenrollPage.displayName = 'ProgramReenrollPage';
